import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {CodaltComponent} from '../../codalt.component';
import {Platform} from '../../classes/platform';
import {PlatformService} from '../../services/platform.service';
import {CheckpointService} from '../../services/checkpoint.service';
import {Checkpoint, level} from '../../classes/checkpoint';
import {MatButton} from '@angular/material/button';
import {CheckpointLevelStatusPipe} from '../../pipes/checkpoint-level-status.pipe';
import {DatePipe, LowerCasePipe} from '@angular/common';
import {Routenames} from '../../classes/route-name.enum';
import {Server} from '../../classes/server';
import {HasFeaturePipe} from '../../pipes/has-feature.pipe';
import {UrlPrefixStrippingPipe} from '../../pipes/url-prefix-stripping.pipe';
import {LastCheckReadablePipe} from '../../pipes/last-check-readable.pipe';
import {DaysFromNowPipe} from '../../pipes/days-from-now.pipe';
import {Title} from '@angular/platform-browser';
import {browserTitle} from '../../page-title.strategy';
import {Subscription} from 'rxjs';
import {CheckpointStatusOrbsEnum} from '../../classes/checkpoint-status-orbs.enum';
import {
    BreadcrumbsComponent,
    DataListComponent,
    DataRowComponent,
    EmptyStateComponent,
    Env,
    EnvBadgeComponent,
    PageComponent,
    PageHeaderComponent,
    RelativeDayPipe,
    SectionComponent,
    StatusBadgeComponent,
    StatusDotComponent,
    TabGroupComponent,
    TabOption,
    Tone
} from '../../shared/ui';
import {
    calendarDaysFromToday,
    envName,
    orbDisplay,
    platformEnvUrls,
    ServerDisplay,
    serverDisplay,
    SSL_WARN_DAYS,
    sslTone,
    StatusDisplay
} from '../seire-display';

const LEVEL_PRIORITY: Record<string, number> = {
    [level.PRODUCTION]: 1,
    [level.HEALTH]: 2,
    [level.CHECK]: 3,
    [level.ACCEPTANCE]: 4,
};

/** Colors of the level pill (design: production green, health blue, check grey, acceptance orange). */
const LEVEL_TONES: Record<string, Tone> = {
    [level.PRODUCTION]: 'ok',
    [level.HEALTH]: 'info',
    [level.CHECK]: 'neutral',
    [level.ACCEPTANCE]: 'warn',
};

const SERVER_ENVS: Record<string, Env> = {Production: 'P', Acceptance: 'A', Test: 'T'};

interface CheckpointRow {
    checkpoint: Checkpoint;
    status: StatusDisplay;
    /** Only filled when the checkpoint is in error. */
    problem: string | null;
    /** Path first (or the host when there is no path), the host below it. */
    main: string;
    host: string | null;
    sslTone: 'error' | 'warn' | null;
    sslWarn: boolean;
}

interface ServerCard {
    display: ServerDisplay;
    envs: Env[];
}

interface Summary {
    tone: Tone;
    label: string;
    sub: string;
}

@Component({
    selector: 'app-platform-checkpoints',
    templateUrl: './platform-checkpoints.component.html',
    styleUrls: ['./platform-checkpoints.component.scss'],
    imports: [
        RouterLink,
        DatePipe,
        LowerCasePipe,
        MatButton,
        HasFeaturePipe,
        LastCheckReadablePipe,
        DaysFromNowPipe,
        BreadcrumbsComponent,
        DataListComponent,
        DataRowComponent,
        EmptyStateComponent,
        EnvBadgeComponent,
        PageComponent,
        PageHeaderComponent,
        RelativeDayPipe,
        SectionComponent,
        StatusBadgeComponent,
        StatusDotComponent,
        TabGroupComponent,
    ]
})
export class PlatformCheckpointsComponent extends CodaltComponent implements OnInit, OnDestroy {

    readonly platform = signal<Platform>(new Platform());
    readonly platforms = signal<Platform[]>([]);
    readonly servers = signal<{ name: string, server: Server }[]>([]);
    readonly checkpoints = signal<Checkpoint[]>(null);
    readonly isAll = signal(false);
    /** Level filter (segmented control); starts at the `?level=` query param when given. */
    readonly levelFilter = signal<string>('all');

    // Subscriptions of the current route params, replaced when navigating to another platform
    private routeSubscriptions = new Subscription();

    private readonly levelStatusPipe = new CheckpointLevelStatusPipe();
    private readonly urlPrefixStripping = new UrlPrefixStrippingPipe();

    readonly sortedCheckpoints = computed(() => {
        const list = this.checkpoints();
        if (!list?.length) {
            return list;
        }
        return [...list].sort((a, b) => {
            const levelDiff = (LEVEL_PRIORITY[a.level] ?? 99) - (LEVEL_PRIORITY[b.level] ?? 99);
            if (levelDiff !== 0) {
                return levelDiff;
            }
            return this.getStatusPriority(a) - this.getStatusPriority(b);
        });
    });

    readonly rows = computed<CheckpointRow[]>(() => (this.sortedCheckpoints() ?? []).map(checkpoint => this.toRow(checkpoint)));

    readonly visibleRows = computed(() => {
        const filter = this.levelFilter();
        return this.rows().filter(row => filter === 'all' || row.checkpoint.level === filter);
    });

    readonly levelOptions = computed<TabOption[]>(() => {
        const rows = this.rows();
        const count = (key: string) => rows.filter(row => row.checkpoint.level === key).length;
        return [
            {value: 'all', label: 'Alle', count: rows.length},
            ...Object.values(level)
                .filter(key => count(key) > 0 || key === this.levelFilter())
                .map(key => ({value: key, label: key, count: count(key)}))
        ];
    });

    readonly checkpointCount = computed(() => {
        const n = this.checkpoints()?.length;
        return n === undefined ? null : `${n} checkpoint${n === 1 ? '' : 's'}`;
    });

    readonly envUrls = computed(() => platformEnvUrls(this.platform()));

    /** Status pill and summary line in the header, derived from the checkpoint statuses. */
    readonly summary = computed<Summary | null>(() => {
        if (this.checkpoints() === null) {
            return null;
        }
        const rows = this.rows();
        const withStatus = (orb: CheckpointStatusOrbsEnum) => rows.filter(row => row.status.rank === orbDisplay(orb).rank).length;
        const errors = withStatus(CheckpointStatusOrbsEnum.error);
        const warnings = withStatus(CheckpointStatusOrbsEnum.warning);
        const postponed = withStatus(CheckpointStatusOrbsEnum.alert);
        const ok = withStatus(CheckpointStatusOrbsEnum.success);
        const ssl = rows.filter(row => row.sslWarn).length;

        const parts: string[] = [];
        if (rows.length) {
            parts.push(ok === rows.length
                ? (rows.length === 1 ? '1 checkpoint goed' : `Alle ${rows.length} checkpoints goed`)
                : `${ok} van ${rows.length} checkpoints goed`);
        }
        if (postponed) {
            parts.push(`${postponed} opgeschort`);
        }
        if (ssl) {
            parts.push(ssl === 1
                ? `1 SSL-certificaat verloopt binnen ${SSL_WARN_DAYS} dagen`
                : `${ssl} SSL-certificaten verlopen binnen ${SSL_WARN_DAYS} dagen`);
        }
        const sub = parts.join(' · ');

        if (errors) {
            return {tone: 'error', label: `${errors} storing${errors === 1 ? '' : 'en'}`, sub};
        }
        if (warnings) {
            return {tone: 'warn', label: `${warnings} waarschuwing${warnings === 1 ? '' : 'en'}`, sub};
        }
        if (postponed && !ok) {
            return {tone: 'warn', label: `${postponed} opgeschort`, sub};
        }
        if (ok) {
            return {tone: 'ok', label: 'Alles goed', sub};
        }
        return {tone: 'neutral', label: 'Geen checks', sub};
    });

    /** One card per unique server, with the environments that run on it. */
    readonly serverCards = computed<ServerCard[]>(() => {
        const cards = new Map<string, ServerCard>();
        for (const {name, server} of this.servers()) {
            if (!server) {
                continue;
            }
            const card = cards.get(server.ip) ?? {display: serverDisplay(server), envs: []};
            card.envs.push(SERVER_ENVS[name]);
            cards.set(server.ip, card);
        }
        return [...cards.values()];
    });

    readonly serversSubtitle = computed(() => {
        const cards = this.serverCards();
        if (!cards.length) {
            return null;
        }
        const envs = (['P', 'A', 'T'] as Env[])
            .filter(env => cards.some(card => card.envs.includes(env)))
            .map(env => envName(env).toLowerCase());
        const list = envs.length > 1 ? `${envs.slice(0, -1).join(', ')} en ${envs[envs.length - 1]}` : envs[0];
        return `${cards.length} server${cards.length === 1 ? '' : 's'} voor ${list}`;
    });

    readonly levelTones = LEVEL_TONES;

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointService: CheckpointService,
        private platformService: PlatformService,
        private titleService: Title,
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.activatedRoute.queryParams.subscribe((params: { level }) => {
                this.levelFilter.set(params.level ?? 'all');
            })
        );

        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { platformId }) => {
                // Until the platform is loaded and its name is known
                this.titleService.setTitle(browserTitle('Seire'));
                const isAll = params.platformId === 'all';
                this.isAll.set(isAll);
                this.routeSubscriptions.unsubscribe();
                this.routeSubscriptions = new Subscription();
                this.subscriptions.add(this.routeSubscriptions);
                this.checkpoints.set(null);

                const platformId = isAll ? null : +params.platformId;

                this.routeSubscriptions.add(
                    this.checkpointService.getCheckpointsLive(platformId).subscribe({
                        next: checkpoints => {
                            this.checkpoints.set(checkpoints);
                        },
                        error: err => console.error(err)
                    })
                );

                if (isAll) {
                    this.crumbs = [
                        {name: 'Seire', route: Routenames.seire},
                        {name: 'Alle checkpoints', route: `${Routenames.seire}/all`}
                    ];
                    this.titleService.setTitle(browserTitle('Alle checkpoints - Seire'));

                    this.routeSubscriptions.add(
                        this.platformService.getPlatformsLive().subscribe({
                            next: platforms => this.platforms.set(platforms),
                            error: err => console.error(err)
                        })
                    );
                    return;
                }

                this.routeSubscriptions.add(
                    this.platformService.liveItem(platformId, this.platformService.getPlatform(platformId)).subscribe({
                        next: platform => this.setPlatform(platform),
                        error: err => console.error(err)
                    })
                );
            })
        );
    }

    private setPlatform(platform: Platform) {
        this.platform.set(platform);

        this.servers.set([
            {name: 'Production', server: platform.prod_server},
            {name: 'Acceptance', server: platform.acc_server},
            {name: 'Test', server: platform.test_server}
        ].filter(p => p.server !== null));

        this.crumbs = [
            {name: 'Seire', route: Routenames.seire},
            {name: `Platform : ${this.platform().name}`, route: `${Routenames.seire}/${this.platform().id}`}
        ];
        this.titleService.setTitle(browserTitle(`${this.platform().name} - Seire`));
    }

    platformName(platformId: number): string {
        return this.platforms().find(platform => +platform.id === +platformId)?.name ?? '';
    }

    getStatusPriority(checkpoint: Checkpoint): number {
        if (checkpoint.checkpoint_results_count === 0) {
            return 5; // inactive
        }

        if (checkpoint.postponed_till && new Date(checkpoint.postponed_till) > new Date()) {
            return 3; // postponed
        }

        const lastInstance = checkpoint.checkpoint_result_last_instance ?? null;
        const formattedLastInstance = lastInstance ? new Date(lastInstance.created_at) : null;
        const timeBehind = new Date();
        timeBehind.setDate(timeBehind.getDate() - 1);

        if (checkpoint.error || (lastInstance !== null && formattedLastInstance < timeBehind)) {
            return 1; // error
        }

        if (checkpoint.warning) {
            return 2; // warning
        }

        return 4; // ok / success
    }

    private toRow(checkpoint: Checkpoint): CheckpointRow {
        // Status from the existing CheckpointLevelStatusPipe
        const orb = this.levelStatusPipe.transform(checkpoint.level, checkpoint);
        const days = calendarDaysFromToday(checkpoint.ssl_valid_till);
        return {
            checkpoint,
            status: orbDisplay(orb),
            // The API only fills `result` when something is wrong, also for warnings and postponed checkpoints
            problem: checkpoint.checkpoint_result_last_instance?.result || null,
            ...this.splitUrl(checkpoint.url),
            sslTone: sslTone(checkpoint.ssl_valid_till),
            sslWarn: days !== null && days < SSL_WARN_DAYS,
        };
    }

    private splitUrl(url: string | null | undefined): { main: string, host: string | null } {
        if (!url) {
            return {main: '', host: null};
        }
        try {
            const parsed = new URL(url);
            const path = (parsed.pathname + parsed.search + parsed.hash).replace(/\/$/, '');
            return path ? {main: path, host: parsed.host} : {main: parsed.host, host: null};
        } catch {
            return {main: this.urlPrefixStripping.transform(url), host: null};
        }
    }
}
